/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useState, useCallback } from 'react';
import { FormGroup, FormControlLabel, Grid } from '@mui/material';
import MUICheckbox from '../MUICheckbox/MUICheckbox';

interface IAppRights {
  appRightId: number;
  rightName: string;
  rightCode: string;
  isMenu: boolean;
  isSubMenu: boolean;
  hasSubMenu: boolean;
  menuOrder: number;
  roleId: number;
  roleName: string;
  hasRight: boolean;
  parentId: number;
  manuallySelected?: boolean;
  children?: IAppRights[];
}

interface PermissionTabProps {
  page: string;
  menuData: IAppRights[];
  closeModal: () => void;
  onMenuFeatureSelect: (menuFeatureId: number | number[], checked: boolean) => void;
  setAppRightData: (updatedMenuData: IAppRights[]) => void;
  persistedAppRightData?: IAppRights[];
}

function AppRightsTab({
  menuData,
  onMenuFeatureSelect,
  setAppRightData,
  persistedAppRightData,
}: PermissionTabProps): JSX.Element {
  const [menuTree, setMenuTree] = useState<IAppRights[]>([]);

  const buildMenuStructure = (rights: IAppRights[]): IAppRights[] => {
    const rightsMap = new Map<number, IAppRights>();
    rights.forEach((item) => {
      rightsMap.set(item.appRightId, { ...item, children: [] });
    });

    const menuStructure: IAppRights[] = [];
    rights.forEach((item) => {
      const current = rightsMap.get(item.appRightId);
      if (!current) return;

      if (item.parentId === 0) {
        menuStructure.push(current);
      } else {
        const parent = rightsMap.get(item.parentId);
        if (parent) {
          parent.children?.push(current);
        }
      }
    });

    return menuStructure;
  };

  const flattenMenuTree = (nodes: IAppRights[]): IAppRights[] => {
    return nodes.flatMap((node) => {
      const { children, ...rest } = node;
      return [rest, ...(children ? flattenMenuTree(children) : [])];
    });
  };

  useEffect(() => {
    //@ts-ignore
    const structured = buildMenuStructure(persistedAppRightData?.length ? persistedAppRightData : menuData);
    setMenuTree(structured);
  }, [menuData, persistedAppRightData]);

  const updateHasRightRecursively = (item: IAppRights, checked: boolean): void => {
    item.hasRight = checked;
    item.children?.forEach((child) => updateHasRightRecursively(child, checked));
  };

  const setAllParentRightsTrue = (nodes: IAppRights[], childId: number): boolean => {
    for (const node of nodes) {
      if (Array.isArray(node.children) && node.children?.some((child) => child.appRightId === childId)) {
        node.hasRight = true;
        setAllParentRightsTrue(nodes, node.appRightId);
        return true;
      }
      if (node.children && setAllParentRightsTrue(node.children, childId)) {
        return true;
      }
    }
    return false;
  };

  const updateParentHasRightBasedOnChildren = (nodes: IAppRights[]): void => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        updateParentHasRightBasedOnChildren(node.children);
        const anyChildChecked = node.children.some((child) => child.hasRight);
        node.hasRight = anyChildChecked;
      }
    });
  };

  const findParentNode = (nodes: IAppRights[], childId: number): IAppRights | null => {
    for (const node of nodes) {
      if (node.children?.some((child) => child.appRightId === childId)) {
        return node;
      }
      if (node.children) {
        const found = findParentNode(node.children, childId);
        if (found) return found;
      }
    }
    return null;
  };
  const ensureViewRightSelected = (nodes: IAppRights[], childId: number): void => {
    const parent = findParentNode(nodes, childId);
    if (parent) {
      parent.hasRight = true;
  
      const viewSibling = parent.children?.find(
        (sibling) =>
          sibling.rightCode.endsWith('_VIEW') &&
          !sibling.hasRight
      );
  
      if (viewSibling) {
        viewSibling.hasRight = true;
      }
  
      // Recurse upward
      ensureViewRightSelected(nodes, parent.appRightId);
    }
  };
  

  const handleCheckboxChange = useCallback(
    (item: IAppRights, checked: boolean): void => {
      if (item.rightCode === 'DASHBOARD') return;
  
      const newTree = [...menuTree];
  
      const updateNode = (nodes: IAppRights[]): boolean => {
        return nodes.some((node) => {
          if (node.appRightId === item.appRightId) {
            node.hasRight = checked;
  
            if (node.children && node.children.length > 0) {
              updateHasRightRecursively(node, checked);
            }
  
            if (checked) {
              // ✅ Ensure all parents are checked
              setAllParentRightsTrue(newTree, node.appRightId);
              ensureViewRightSelected(newTree, node.appRightId);
              // ✅ Find parent and make sure _VIEW sibling is also checked
              const parent = findParentNode(newTree, node.appRightId);
              if (parent) {
                parent.hasRight = true;
  
                const viewSibling = parent.children?.find(
                  (sibling) =>
                    sibling.rightCode !== item.rightCode &&
                    sibling.rightCode.endsWith('_VIEW')
                );
  
                if (viewSibling && !viewSibling.hasRight) {
                  viewSibling.hasRight = true;
                }
              }
            }
  
            return true;
          }
  
          if (node.children && updateNode(node.children)) {
            return true;
          }
  
          return false;
        });
      };
  
      updateNode(newTree);
  
      if (!checked) {
        const parent = findParentNode(newTree, item.appRightId);
        if (parent) {
          const isView = item.rightCode.endsWith('_VIEW');
      
          if (isView) {
            parent.children?.forEach((sibling) => {
              if (sibling.appRightId !== item.appRightId) {
                sibling.hasRight = false;
              }
            });
      
            parent.hasRight = false;
          }
        }
      
        updateParentHasRightBasedOnChildren(newTree);
      }
      
  
      const collectAllIds = (item: IAppRights): number[] => {
        const childIds = item.children?.flatMap(collectAllIds) ?? [];
        return [item.appRightId, ...childIds];
      };
  
      const allChangedIds = collectAllIds(item);
      onMenuFeatureSelect(allChangedIds, checked);
      setMenuTree(newTree);
      setAppRightData(flattenMenuTree(newTree));
    },
    [menuTree, onMenuFeatureSelect, setAppRightData]
  );
 
  /* eslint-disable react/prop-types */
  // eslint-disable-next-line react/display-name
  const MemoizedRenderCheckboxTree: React.FC<{ data: IAppRights[]; level?: number }> = React.memo(
    ({ data, level = 0 }) => {
      return (
        <>
          {data.map((item) => {
            const isIndeterminate = false;

            return (
              <div key={item.appRightId}>
                <Grid container spacing={0} alignItems="center" sx={{ marginLeft: `${level * 10}px` }}>
                  <Grid item xs={11} sx={{ display: 'flex' }}>
                    <FormGroup
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontWeight: level === 0 ? 'bold' : 'normal',
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <MUICheckbox
                            checked={item.rightCode === 'DASHBOARD' ? true : item.hasRight}
                            disabled={item.rightCode === 'DASHBOARD'}
                            indeterminate={isIndeterminate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              e.target.blur();
                              handleCheckboxChange(item, e.target.checked);
                            }}
                            sx={{
                              color: isIndeterminate ? '#000000' : undefined,
                              '&.MuiCheckbox-indeterminate': {
                                color: '#000000',
                              },
                              '&.Mui-checked': {
                                color: '#d32f2f',
                              },
                            }}
                          />
                        }
                        label={item.rightName}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>

                {item.children && item.children.length > 0 && (
                  <Grid container spacing={0} alignItems="center" sx={{ paddingLeft: `${(level + 1) * 10}px` }}>
                    <Grid item xs={11}>
                      <MemoizedRenderCheckboxTree data={item.children} level={level + 1} />
                    </Grid>
                  </Grid>
                )}

                {level === 0 && (
                  <hr
                    style={{
                      border: 'none',
                      borderBottom: '1px solid #e0e0e0',
                      width: '100%',
                      margin: '4px 0',
                    }}
                  />
                )}
              </div>
            );
          })}
        </>
      );
    }
  );
 
  console.log(menuTree);
  return (
    <div className="px-[16px] pt-[10px] mb-[60px] space-y-4">
      <MemoizedRenderCheckboxTree data={menuTree} />
    </div>
  );
}

export default AppRightsTab;
